/**
 * Core Host Main Application Entry Point
 *
 * This is the main Vue application entry point for the Core Host (micro-frontend shell).
 * It initializes Vue 3, configures plugins, and sets up the framework.
 */

import { createApp, type App as VueApp } from 'vue'
import { createPinia } from 'pinia'
import moment from 'moment'
import axios from 'axios'
import VXETable from 'vxe-table'
import VxeUI from 'vxe-pc-ui'
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app'

import VueAxios from 'vue-axios'
import ViewUIPlus from 'view-ui-plus'

// View UI Plus CSS (must be before Tailwind to allow utilities to override)
import 'view-ui-plus/dist/styles/viewuiplus.css'

// VXE Table styles
import 'vxe-pc-ui/lib/style.css'
import 'vxe-table/lib/style.css'

// Design System - Tailwind CSS (after component libraries)
import '../design-system/styles/tailwind.css'

import '@shared/assets/icons/icofont/icofont.min.css'
import '@shared/styles/base.styl'
import '@shared/styles/app.styl'
import '@shared/styles/general-pages.styl'
import '@shared/styles/iview-dark-theme.styl'

import App from './App.vue'

// Date 原型擴展 - 必須在使用前導入
import '@shared/utils/datePrototype'

import globalState from '@shared/stores/globalState'
import fieldScope from '@shared/stores/fieldScope'
import uiState from '@shared/stores/uiState'
import userStore from '@shared/stores/userStore'
import routeApp from './routeApp'
import { firebaseConfig } from '@shared/config/firebaseConfig'
import Swal from 'sweetalert2'

// PWA Service Worker 註冊
import '@shared/registerServiceWorker'

// Vuetify - Tree-shaking configuration
import 'material-design-icons-iconfont/dist/material-design-icons.css'
import 'vuetify/styles'
import { createVuetify, type ThemeDefinition } from 'vuetify'
import { aliases, md } from 'vuetify/iconsets/md'

/**
 * Vuetify light theme configuration
 */
const lightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: '#1976D2',
    secondary: '#424242',
    accent: '#82B1FF',
    error: '#FF5252',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FFC107',
  },
}

/**
 * Firebase global properties interface
 */
interface FirebaseGlobalProperties {
  app: FirebaseApp
}

// Extend Vue's ComponentCustomProperties
declare module 'vue' {
  interface ComponentCustomProperties {
    $firebase: FirebaseGlobalProperties
    $moment: typeof moment
    $swal: typeof Swal
  }
}

// Initialize Firebase
const firebaseApp: FirebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp()

// Initialize Vue
const pinia = createPinia()
const app: VueApp = createApp(App)

const vuetify = createVuetify({
  icons: {
    defaultSet: 'md',
    aliases,
    sets: {
      md,
    },
  },
  defaults: {
    global: {
      ripple: false,
      density: 'compact',
    },
    VBtn: {
      size: 'small',
      density: 'compact',
      variant: 'flat',
    },
    VTextField: {
      density: 'compact',
      variant: 'outlined',
      hideDetails: 'auto',
    },
    VSelect: {
      density: 'compact',
      variant: 'outlined',
      hideDetails: 'auto',
    },
    VCard: {
      elevation: 1,
    },
    VDataTable: {
      density: 'compact',
      itemsPerPage: 25,
    },
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: lightTheme,
    },
  },
})

// Make Firebase services available to components
app.config.globalProperties.$firebase = {
  app: firebaseApp,
}

app
  .use(pinia)
  .use(VXETable)
  .use(vuetify)
  .use(VxeUI)
  .use(VueAxios, axios)
  .use(ViewUIPlus)
  .use(fieldScope)
  .use(uiState)
  .use(userStore)
  .use(routeApp)
  .use(globalState)

moment.locale('zh-tw')

app.config.globalProperties.$moment = moment
app.config.globalProperties.$swal = Swal

// Detect platform
document.body.classList.add('platform-web')

app.mount('#app')
